import React, { createContext, useCallback, useContext, useState } from 'react';

const UIContext = createContext(null);

let _nextId = 1;

export function UIContextProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState(null);
    const [promptState, setPromptState] = useState(null);

    const toast = useCallback((message, type = 'error') => {
        const id = _nextId++;
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    }, []);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const confirm = useCallback((message, { confirmLabel = 'Bestätigen', danger = false } = {}) => {
        return new Promise(resolve => {
            setConfirmState({ message, confirmLabel, danger, resolve });
        });
    }, []);

    const handleConfirmClose = (result) => {
        confirmState?.resolve(result);
        setConfirmState(null);
    };

    // Resolves with the entered text on confirm, or null if cancelled.
    const prompt = useCallback((message, {
        confirmLabel = 'Bestätigen',
        placeholder = '',
        danger = false,
        defaultValue = '',
    } = {}) => {
        return new Promise(resolve => {
            setPromptState({ message, confirmLabel, placeholder, danger, value: defaultValue, resolve });
        });
    }, []);

    const handlePromptClose = (confirmed) => {
        if (!promptState) return;
        promptState.resolve(confirmed ? (promptState.value?.trim() || '') : null);
        setPromptState(null);
    };

    return (
        <UIContext.Provider value={{ toast, confirm, prompt }}>
            {children}

            <div className="toast-stack" aria-live="polite">
                {toasts.map(t => (
                    <div key={t.id} className={`toast toast--${t.type}`} role="alert">
                        <span className="toast-message">{t.message}</span>
                        <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Schließen">×</button>
                    </div>
                ))}
            </div>

            {confirmState && (
                <div className="confirm-backdrop" onClick={() => handleConfirmClose(false)}>
                    <div className="confirm-dialog" role="dialog" onClick={e => e.stopPropagation()}>
                        <p className="confirm-message">{confirmState.message}</p>
                        <div className="confirm-actions">
                            <button className="button" onClick={() => handleConfirmClose(false)}>
                                Abbrechen
                            </button>
                            <button
                                className={`button${confirmState.danger ? ' button-danger' : ''}`}
                                onClick={() => handleConfirmClose(true)}
                            >
                                {confirmState.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {promptState && (
                <div className="confirm-backdrop" onClick={() => handlePromptClose(false)}>
                    <div className="confirm-dialog" role="dialog" onClick={e => e.stopPropagation()}>
                        <p className="confirm-message">{promptState.message}</p>
                        <textarea
                            className="prompt-input"
                            autoFocus
                            rows={3}
                            placeholder={promptState.placeholder}
                            value={promptState.value}
                            onChange={e => setPromptState(prev => ({ ...prev, value: e.target.value }))}
                        />
                        <div className="confirm-actions">
                            <button className="button" onClick={() => handlePromptClose(false)}>
                                Abbrechen
                            </button>
                            <button
                                className={`button${promptState.danger ? ' button-danger' : ''}`}
                                onClick={() => handlePromptClose(true)}
                            >
                                {promptState.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UIContext.Provider>
    );
}

export function useToast() {
    return useContext(UIContext).toast;
}

export function useConfirm() {
    return useContext(UIContext).confirm;
}

export function usePrompt() {
    return useContext(UIContext).prompt;
}
